import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FeedLine } from 'src/app/services/helper.service';
import { PredictionLegendForecast, PredictionLegendGroup } from './prediction-legend.model';

// A swatch-less line (e.g. "Inundated Area : 6.11 sqkm") heads the colored lines after it.
export interface ExtraLegendGroup {
  header?: FeedLine;
  items: FeedLine[];
}

@Component({
  selector: 'app-prediction-legend',
  templateUrl: './prediction-legend.component.html',
  styleUrls: ['./prediction-legend.component.scss'],
})
export class PredictionLegendComponent implements OnChanges {
  @Input() groups: PredictionLegendGroup[] = [];
  @Input() extraItems: FeedLine[] = [];
  // Forecast hour of the prediction polygon hovered on the map.
  @Input() hoveredForecastHour: number | null = null;

  collapsed = false;
  // Forecast hour of the legend item under the cursor; takes priority over the map hover.
  legendHoveredHour: number | null = null;
  extraGroups: ExtraLegendGroup[] = [];
  private expandedGroupIds = new Set<string>();
  private collapsedExtraGroups = new Set<number>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['extraItems']) {
      this.extraGroups = this.buildExtraGroups(this.extraItems ?? []);
    }
    if (!changes['groups']) return;

    const validIds = new Set(this.groups.map((group) => group.id));
    const previousIds = new Set(
      ((changes['groups'].previousValue ?? []) as PredictionLegendGroup[]).map((group) => group.id)
    );
    // Clearing a filter restores detection groups; expand all when groups are added back.
    if (this.groups.some((group) => !previousIds.has(group.id))) {
      this.expandedGroupIds = validIds;
      return;
    }
    const retainedIds = [...this.expandedGroupIds].filter((id) => validIds.has(id));
    this.expandedGroupIds = new Set(retainedIds);
  }

  toggleLegend(): void {
    this.collapsed = !this.collapsed;
  }

  toggleGroup(group: PredictionLegendGroup): void {
    if (this.expandedGroupIds.has(group.id)) {
      this.expandedGroupIds.delete(group.id);
    } else {
      this.expandedGroupIds.add(group.id);
    }
  }

  isExpanded(group: PredictionLegendGroup): boolean {
    return this.expandedGroupIds.has(group.id);
  }

  toggleExtraGroup(index: number): void {
    if (this.collapsedExtraGroups.has(index)) {
      this.collapsedExtraGroups.delete(index);
    } else {
      this.collapsedExtraGroups.add(index);
    }
  }

  hoveredForecast(group: PredictionLegendGroup): PredictionLegendForecast | undefined {
    const hour = this.legendHoveredHour ?? this.hoveredForecastHour;
    return hour == null ? undefined
      : group.forecasts.find((forecast) => forecast.hour === hour && forecast.area);
  }

  isExtraGroupExpanded(index: number): boolean {
    return !this.collapsedExtraGroups.has(index);
  }

  private buildExtraGroups(items: FeedLine[]): ExtraLegendGroup[] {
    const groups: ExtraLegendGroup[] = [];
    for (const item of items) {
      const current = groups[groups.length - 1];
      if (!item.legendColor || !current) {
        groups.push(item.legendColor ? { items: [item] } : { header: item, items: [] });
      } else {
        current.items.push(item);
      }
    }
    return groups;
  }

  trackGroup(_: number, group: PredictionLegendGroup): string {
    return group.id;
  }

  trackForecast(_: number, forecast: { hour: number }): number {
    return forecast.hour;
  }

  // extraItems is rebuilt on every change detection, so keep the group DOM by position;
  // otherwise the header button is replaced between mousedown and click and the click is lost.
  trackExtraGroup(index: number): number {
    return index;
  }

  trackExtraItem(index: number, item: FeedLine): string {
    return `${item.text}-${index}`;
  }
}
